import { useState, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import { updateUploadProgress } from '../components/common/UploadProgress';

/**
 * Custom hook for handling file uploads with progress tracking
 * 
 * SUPPORTED FILE TYPES (up to 100MB each, max 10 files per upload):
 * - Documents: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Excel (.xls, .xlsx), Text, CSV
 * - Images: JPEG, PNG, GIF  
 * - Compressed: ZIP, RAR, 7Z, TAR, GZIP, BZIP2, XZ
 * 
 * VALIDATION APPROACH:
 * - Hybrid MIME type validation (primary) with extension fallback
 * - Accepts generic MIME types like 'application/octet-stream' for browser compatibility
 * - File size limit: 100MB per file
 * - Quantity limit: Up to 10 files per upload
 * 
 * @param {boolean} isAdminUpload - Whether to use admin upload endpoint
 * @param {string} fieldName - Unique identifier for the upload field
 */
export const useFileUpload = (isAdminUpload = false, fieldName = 'default') => {
  const [state, setState] = useState({
    uploading: false,
    progress: 0,
    uploadedFiles: [],
    error: null,
  });

  const endpoint = isAdminUpload ? '/api/files/admin/upload' : '/api/files/upload';

  // Sync upload progress with global state for UI components
  useEffect(() => {
    updateUploadProgress(fieldName, {
      uploading: state.uploading,
      progress: state.progress
    });
  }, [state.uploading, state.progress, fieldName]);

  /**
   * Validate file type and size before upload
   * @param {File} file - The file to validate
   * @param {number} maxSizeMB - Maximum file size in MB (default: 100MB)
   * @returns {object} - { isValid: boolean, error: string|null, warnings: string[] }
   */
  const validateFile = useCallback((file, maxSizeMB = 100) => {
    if (!file) {
      return { isValid: false, error: 'No file provided', warnings: [] };
    }

    const warnings = [];
    
    // File size validation with custom limit
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return { 
        isValid: false, 
        error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds ${maxSizeMB}MB limit`, 
        warnings 
      };
    }

    // Supported extensions
    const supportedExtensions = [
      'pdf', 'doc', 'docx', 'ppt', 'pptx', 'txt', 'csv', 'xlsx', 'xls',
      'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'
    ];

    // Supported MIME types (including common variations)
    const supportedMimeTypes = [
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv',
      
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      
      // Compressed
      'application/zip', 'application/x-zip-compressed',
      'application/vnd.rar', 'application/x-rar-compressed',
      'application/x-7z-compressed', 'application/x-tar',
      'application/gzip', 'application/x-bzip2', 'application/x-xz',
      
      // Generic MIME types (browser compatibility)
      'application/octet-stream', 'application/x-download', 'binary/octet-stream'
    ];

    // Get file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    
    // Check MIME type
    const mimeType = file.type.toLowerCase();
    const isMimeSupported = supportedMimeTypes.some(type => mimeType.includes(type));
    const isExtensionSupported = supportedExtensions.includes(fileExtension);
    
    // Generic MIME type handling (for browser compatibility)
    const isGenericMime = ['application/octet-stream', 'application/x-download', 'binary/octet-stream']
      .includes(mimeType);

    if (isGenericMime && isExtensionSupported) {
      warnings.push(`Generic MIME type detected (${mimeType}), but extension is valid (.${fileExtension})`);
    } else if (!isMimeSupported && !isExtensionSupported) {
      return { 
        isValid: false, 
        error: `Unsupported file type: .${fileExtension} (${mimeType}). Supported types: ${supportedExtensions.join(', ')}`, 
        warnings 
      };
    } else if (!isMimeSupported && isExtensionSupported) {
      warnings.push(`MIME type not recognized (${mimeType}), but extension (.${fileExtension}) is supported`);
    }

    return { isValid: true, error: null, warnings };
  }, []);

    const uploadSingleFile = useCallback(async (file, onProgress = null, skipValidation = false) => {
    if (!file) {
      toast.error('Please select a file to upload', {
        position: "top-center"
      });
      return null;  
    }

    // Validate file unless explicitly skipped
    if (!skipValidation) {
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.error, {
          position: "top-center"
        });
        return null;
      }
      
      // Show warnings if any
      validation.warnings.forEach(warning => {
        console.warn('File upload warning:', warning);
      });
    }

    const formData = new FormData();
    formData.append('file', file);

    // Set uploading state immediately
    flushSync(() => {
      setState(prev => ({
        ...prev,
        uploading: true,
        progress: 0,
        error: null,
      }));
    });

    try {
      const response = await axiosInstance.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes for large files
        onUploadProgress: (event) => {
          const progress = Math.round((event.loaded * 100) / event.total);
          
          // Update progress immediately
          flushSync(() => {
            setState(prev => ({ ...prev, progress }));
          });

          // Notify custom callback if provided
          if (onProgress) {
            onProgress(progress);
          }
        },
      });

      if (response.data.success) {
        const serverFile = response.data.data;
        
        // Normalize file structure for consistent usage
        const normalizedFile = {
          ...serverFile,
          name: serverFile.original_name || serverFile.name,
          size: Number(serverFile.file_size || serverFile.size || 0),
          type: serverFile.mime_type || serverFile.type,
        };
        
        // Show 100% completion briefly
        flushSync(() => {
          setState(prev => ({ ...prev, progress: 100 }));
        });
        
        // Brief pause to show completion
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Reset to idle state
        flushSync(() => {
          setState(prev => ({
            ...prev,
            uploading: false,
            progress: 0,
            uploadedFiles: [...prev.uploadedFiles, normalizedFile],
            error: null,
          }));
        });

        toast.success('File uploaded successfully!', {
          position: "top-center"
        });

        return normalizedFile;
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'File upload failed';

      // Reset to error state
      flushSync(() => {
        setState(prev => ({
          ...prev,
          uploading: false,
          progress: 0,
          error: errorMessage,
        }));
      });

      // Note: Error toast is handled by axios interceptor for consistency
      return null;
    }
  }, [endpoint, validateFile]);

  const uploadMultipleFiles = useCallback(async (files, onProgress = null) => {
    if (!files || files.length === 0) {
      toast.error('Please select at least one file to upload', {
        position: "top-center"
      });
      return [];
    }

    const uploadPromises = files.map((file, index) => 
      uploadSingleFile(file, (progress) => {
        if (onProgress) {
          // Calculate combined progress across all files
          const overallProgress = ((index * 100) + progress) / files.length;
          onProgress(Math.round(overallProgress));
        }
      })
    );

    try {
      const results = await Promise.allSettled(uploadPromises);
      const successfulUploads = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      const failedCount = results.filter(result => result.status === 'rejected').length;
      
      if (failedCount > 0) {
        toast.warning(
          `${successfulUploads.length} of ${files.length} files uploaded successfully`,
          { position: "top-center" }
        );
      }

      return successfulUploads;
    } catch (error) {
      // Note: Error toast is handled by axios interceptor for consistency
      return [];
    }
  }, [uploadSingleFile]);

  const resetState = useCallback(() => {
    setState({
      uploading: false,
      progress: 0,
      uploadedFiles: [],
      error: null,
    });
  }, []);

  const removeUploadedFile = useCallback((fileId) => {
    setState(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(file => file.id !== fileId),
    }));
  }, []);

  return {
    // Upload state
    uploading: state.uploading,
    uploadProgress: state.progress, // Keep original name for compatibility
    uploadedFiles: state.uploadedFiles,
    error: state.error,
    
    // Upload functions
    uploadFile: uploadSingleFile, // Keep original name for compatibility
    uploadMultipleFiles,
    resetUploadState: resetState, // Keep original name for compatibility
    removeUploadedFile,
    
    // File validation
    validateFile,
  };
};

export default useFileUpload;
